import axios from 'axios';
import env from '../config/env.js';

// ✅ CONFIGURAÇÃO - Container Unificado (Backend + Frontend)
// IMPORTANTE: Em produção/homolog, sempre usar caminho relativo '/api' (mesma origem)
// O backend serve o frontend, então estão na mesma origem (SEM CORS necessário)
// REGRA DO PROJETO: NÃO USAMOS CORS em produção - sempre usar caminho relativo
// Em desenvolvimento local: usar URL absoluta apenas se necessário (quando não há proxy)
const getApiBaseUrl = () => {
  // Em `react-scripts start`, NODE_ENV permanece development; o perfil da
  // aplicação pode ser explicitamente definido pelos scripts locais.
  const NODE_ENV = process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV || 'development';
  
  // ✅ Em produção/homolog/staging: sempre usar caminho relativo (container unificado)
  if (NODE_ENV === 'production' || NODE_ENV === 'homolog' || NODE_ENV === 'staging') {
    return '/api';
  }
  
  // ✅ Em desenvolvimento: verificar se há variável de ambiente para URL absoluta
  // Se não houver, usar caminho relativo (assumindo proxy configurado)
  // Se REACT_APP_API_BASE_URL estiver definido, usar (frontend/backend separados sem proxy)
  if (process.env.REACT_APP_API_BASE_URL) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    return apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
  }
  
  // Se REACT_APP_API_URL_DEV estiver definido, usar URL absoluta
  if (process.env.REACT_APP_API_URL_DEV) {
    const devUrl = process.env.REACT_APP_API_URL_DEV;
    return devUrl.endsWith('/api') ? devUrl : `${devUrl}/api`;
  }
  
  // ✅ Padrão: usar caminho relativo (assumindo proxy reverso configurado)
  return '/api';
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: parseInt(env.API_TIMEOUT),
  headers: {
    'accept': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // ✅ Lista de rotas públicas que NÃO precisam de token
    const publicRoutes = [
      '/login',
      '/health',
      'auth/login',
      'auth/forgot-password',
      'auth/reset-password',
      'auth/verify-reset-token',
      'auth/contact'
    ];
    
    // ✅ Verificar se a rota é pública
    // A URL pode vir como '/auth/forgot-password' ou 'auth/forgot-password' (com ou sem baseURL)
    const url = String(config.url || '').toLowerCase().trim();
    const baseURL = String(config.baseURL || '').toLowerCase().trim();
    
    // Construir URL completa para verificação
    const fullUrl = (baseURL + url).toLowerCase();
    
    // Verificar se é rota pública - verificação simples e direta
    const isPublicRoute = publicRoutes.some(route => {
      // Evita tratar o módulo autenticado `/health-tables` como health check.
      if (route === '/health') {
        return url === '/health' || fullUrl.endsWith('/health');
      }
      // Verificar se a URL contém a rota pública (de forma flexível)
      return fullUrl.includes(route) || url.includes(route);
    });
    
    // ✅ ADICIONAR TOKEN APENAS SE EXISTIR E NÃO FOR ROTA PÚBLICA
    // IMPORTANTE: Para rotas públicas, NÃO adicionar o header Authorization mesmo se houver token
    if (isPublicRoute) {
      // Para rotas públicas, garantir que NÃO há header Authorization
        delete config.headers.Authorization;
      // Log apenas em desenvolvimento para debug
      if (process.env.NODE_ENV === 'development') {
        console.log('🔓 Rota pública detectada, token não será enviado:', {
          url: url,
          fullUrl: fullUrl,
          baseURL: baseURL,
          hasToken: !!token
        });
      }
    } else if (token) {
      // Para rotas privadas, adicionar token se existir
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Log apenas em desenvolvimento para debug
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ Rota privada sem token:', url);
      }
    }

    // ✅ HEADER OBRIGATÓRIO PARA TODAS AS REQUISIÇÕES
    config.headers['x-second-key'] = env.SECOND_SECRET_KEY;
    
    // ✅ CONTENT-TYPE PARA REQUISIÇÕES POST/PUT/PATCH
    if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Se a resposta for bem-sucedida (200-299), retornar diretamente
    return response;
  },
  (error) => {
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error("O servidor demorou muito para responder. Por favor, tente novamente."));
    }

    if (error.response) {
      // Para rotas públicas, não limpar token nem redirecionar em caso de 401.
      const errorUrl = String(error.config?.url || '').toLowerCase();
      const isPublicRoute = errorUrl === '/login' ||
               errorUrl === '/health' ||
               errorUrl.includes('auth/login') ||
                           error.config?.url?.toLowerCase().includes('auth/forgot-password') ||
                           error.config?.url?.toLowerCase().includes('auth/reset-password') ||
                           error.config?.url?.toLowerCase().includes('auth/verify-reset-token') ||
                           error.config?.url?.toLowerCase().includes('auth/contact');
      
      // 403 significa que o usuário autenticado não possui permissão para aquele
      // recurso. Não é falha de autenticação e, portanto, não pode encerrar a
      // sessão nem remover o tenant armazenado. Somente 401 invalida a sessão.
      const deveEncerrarSessao = error.response.status === 401;

      if (deveEncerrarSessao && !isPublicRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('st_codHashEmpresa');
        localStorage.removeItem('usuario');
        
        // Só redireciona se não estiver já na página de login
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      
      // Retornar os dados da resposta de erro para tratamento no componente
        return Promise.reject({
        ...error.response.data,
        status: error.response.status,
        response: error.response
        });
    }

    if (error.request) {
      return Promise.reject(new Error("Não foi possível conectar ao servidor. Verifique sua conexão."));
    }

    return Promise.reject(error);
  }
);

export default api;
